<script setup lang="ts">
import type { HorizontalMenuProps } from './horizontal-menu';
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import ProFeatureBadge from '../ProFeatureBadge/ProFeatureBadge.vue';

const props = withDefaults(defineProps<HorizontalMenuProps>(), {
  breakpoint: 'sm',
});

const emits = defineEmits<{
  selectTab: [tab: string];
}>();

const isLabelHidden = (tabId: string): boolean => tabId !== props.activeTabId;

const NUMBER_BADGE_CLASS =
  'bg-primary-50 text-primary-700 flex h-5 min-w-5 items-center justify-center rounded px-1.5 text-xs';
</script>

<template>
  <div class="flex gap-8">
    <div
      v-for="tab in props.tabs"
      :id="tab.id"
      :key="tab.label"
      type="button"
      :class="[
        'tab-underline cursor-pointer whitespace-nowrap',
        tab.id === props.activeTabId
          ? 'text-primary-500 tab-underline-active active rounded-t-md bg-white'
          : 'text-slate-500 hover:text-neutral-900',
      ]"
      @click="emits('selectTab', tab.id)"
    >
      <div
        class="flex items-center gap-1 text-sm font-semibold transition-all ease-out hover:translate-y-[-4px]"
        :class="[props.compact ? 'py-2' : 'py-4']"
      >
        <ProFeatureBadge
          v-if="tab.showProBadge && isLabelHidden(tab.id)"
          :show="true"
          :class="`inline-flex ${props.breakpoint}:hidden`"
        >
          <span v-if="tab.number !== undefined" :class="NUMBER_BADGE_CLASS">
            {{ tab.number }}
          </span>
          <HugeIcon v-else-if="tab.icon" :icon="tab.icon" :size="20" />
        </ProFeatureBadge>
        <span v-else-if="tab.number !== undefined" :class="NUMBER_BADGE_CLASS">
          {{ tab.number }}
        </span>
        <HugeIcon v-else-if="tab.icon" :icon="tab.icon" :size="20" />

        <ProFeatureBadge
          v-if="tab.showProBadge"
          :show="true"
          placement="corner"
          :class="[
            isLabelHidden(tab.id)
              ? `hidden ${props.breakpoint}:inline-flex`
              : 'inline-flex',
          ]"
        >
          <span>{{ tab.label }}</span>
        </ProFeatureBadge>
        <div
          v-else
          :class="[isLabelHidden(tab.id) && `hidden ${props.breakpoint}:block`]"
        >
          {{ tab.label }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tab-underline {
  position: relative;
}

/* Pseudo-element underline */
.tab-underline::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 2px;
  background-color: var(--color-primary-500);
  opacity: 0;
  transition: opacity 200ms ease-out;
}

/* Active state */
.tab-underline-active::after {
  opacity: 1;
}
</style>
